import { Button, Checkbox, Empty, List, Typography, message, Select, Space } from "antd";
import { CheckCircle, X, AlertTriangle } from "lucide-react";
import React, { useState, useEffect } from "react";
import { AddOnServiceDto } from "~/services/add-on-services/add-on-services.types";
import { TServiceTicket } from "~/types";

const { Text, Title } = Typography;

// Task status options
type TaskStatus = 'pending' | 'completed' | 'removed';

const TaskStatusOptions = [
  { value: 'pending', label: 'Pending', color: '#6b7280' },
  { value: 'completed', label: 'Completed', color: '#059669' },
  { value: 'removed', label: 'Removed from Scope', color: '#dc2626' },
];

interface TaskCheckListProps {
  serviceTicket: TServiceTicket;
  onTasksChecked?: (taskIds: string[], allChecked: boolean, completedCount?: number, taskStatuses?: Record<string, TaskStatus>) => void;
  disableActions?: boolean;
}

export const TaskCheckList: React.FC<TaskCheckListProps> = ({
  serviceTicket,
  onTasksChecked,
  disableActions = false,
}) => {
  const [taskStatuses, setTaskStatuses] = useState<Record<string, TaskStatus>>({});

  // Initialize status state for tasks
  useEffect(() => {
    if (serviceTicket?.addOnServices?.length) {
      const initialStatusState: Record<string, TaskStatus> = {};
      serviceTicket.addOnServices.forEach((task) => {
        initialStatusState[task.id] = 'pending';
      });
      setTaskStatuses(initialStatusState);

    }
  }, [serviceTicket]);

  // Notify parent of completed count whenever task statuses change
  useEffect(() => {
    if (onTasksChecked && Object.keys(taskStatuses).length > 0) {
      const completedCount = Object.values(taskStatuses).filter(status => status === 'completed').length;
      const allResolved = Object.values(taskStatuses).every(status =>
        status === 'completed' || status === 'removed'
      ) && Object.keys(taskStatuses).length === serviceTicket?.addOnServices?.length;
      onTasksChecked([], allResolved, completedCount, taskStatuses);
    }
  }, [taskStatuses, onTasksChecked, serviceTicket?.addOnServices?.length]);

  const handleTaskStatusChange = (taskId: string, status: TaskStatus) => {
    const newTaskStatuses = { ...taskStatuses, [taskId]: status };
    setTaskStatuses(newTaskStatuses);

    // Check if all tasks are completed or removed from scope
    const allResolved = Object.values(newTaskStatuses).every(status =>
      status === 'completed' || status === 'removed'
    ) && Object.keys(newTaskStatuses).length === serviceTicket?.addOnServices?.length;

    // Note: onTasksChecked will be called by the useEffect that watches taskStatuses

    const statusLabels = {
      completed: 'completed',
      removed: 'removed from scope',
      pending: 'marked as pending'
    };

    message.success(`Task ${statusLabels[status]}`);
  };

  const handleBulkStatusChange = (status: TaskStatus) => {
    if (!serviceTicket?.addOnServices?.length) return;

    const newTaskStatuses: Record<string, TaskStatus> = {};
    const taskIds: string[] = [];

    serviceTicket.addOnServices.forEach((task) => {
      newTaskStatuses[task.id] = status;
      taskIds.push(task.id);
    });

    setTaskStatuses(newTaskStatuses);

    const allResolved = status === 'completed' || status === 'removed';
    // Note: onTasksChecked will be called by the useEffect that watches taskStatuses

    const statusLabels = {
      completed: 'completed',
      removed: 'removed from scope',
      pending: 'marked as pending'
    };

    message.success(`All tasks ${statusLabels[status]}`);
  };

  // Get task status counts
  const getStatusCounts = () => {
    const counts = { pending: 0, completed: 0, removed: 0 };
    Object.values(taskStatuses).forEach(status => {
      counts[status]++;
    });
    return counts;
  };

  const statusCounts = getStatusCounts();
  const allResolved = serviceTicket?.addOnServices &&
    serviceTicket.addOnServices.length > 0 &&
    statusCounts.pending === 0;

  if (!serviceTicket?.addOnServices?.length) {
    return <Empty description="No tasks found for this service ticket" />;
  }

  return (
    <div className="task-check-list">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Title level={5} className="mb-0">
            Required Tasks ({serviceTicket.addOnServices?.length || 0})
          </Title>
          <div className="text-sm text-gray-500 mt-1">
            Completed: {statusCounts.completed} • Removed: {statusCounts.removed} • Pending: {statusCounts.pending}
          </div>
        </div>
        {!disableActions && (
          <Space>
            <Button
              size="small"
              onClick={() => handleBulkStatusChange('completed')}
              disabled={disableActions}
            >
              Mark All Complete
            </Button>
            <Button
              size="small"
              onClick={() => handleBulkStatusChange('pending')}
              disabled={disableActions}
            >
              Reset All
            </Button>
          </Space>
        )}
      </div>

      {allResolved && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
          <CheckCircle size={16} className="text-green-600 mr-2" />
          <Text className="text-green-800 font-medium">
            All required tasks have been completed or removed from scope
          </Text>
        </div>
      )}

      <List
        itemLayout="horizontal"
        dataSource={serviceTicket.addOnServices || []}
        renderItem={(task: AddOnServiceDto) => {
          const currentStatus = taskStatuses[task.id] || 'pending';
          const statusOption = TaskStatusOptions.find(opt => opt.value === currentStatus);

          return (
            <List.Item
              key={task.id}
              actions={[
                <Select
                  value={currentStatus}
                  onChange={(value) => handleTaskStatusChange(task.id, value as TaskStatus)}
                  disabled={disableActions}
                  style={{ width: 180 }}
                  size="small"
                >
                  {TaskStatusOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      <span style={{ color: option.color }}>
                        {option.label}
                      </span>
                    </Select.Option>
                  ))}
                </Select>,
              ]}
            >
              <List.Item.Meta
                avatar={
                  currentStatus === 'completed' ? (
                    <CheckCircle size={16} className="text-green-600 mt-1" />
                  ) : currentStatus === 'removed' ? (
                    <X size={16} className="text-red-600 mt-1" />
                  ) : (
                    <AlertTriangle size={16} className="text-yellow-600 mt-1" />
                  )
                }
                title={
                  <div className="flex items-center">
                    <span className={currentStatus !== 'pending' ? 'line-through text-gray-500' : ''}>
                      {task.name}
                    </span>
                    {statusOption && (
                      <span
                        className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: `${statusOption.color}20`,
                          color: statusOption.color
                        }}
                      >
                        {statusOption.label}
                      </span>
                    )}
                  </div>
                }
                description={
                  <div>
                    <Text type="secondary">{task.description || "No description available"}</Text>
                    <div className="mt-1">
                      <Text type="secondary" className="text-xs">
                        Type: {task.type}
                      </Text>
                      {task.unit && (
                        <Text type="secondary" className="text-xs ml-2">
                          Unit: {task.unit}
                        </Text>
                      )}
                    </div>
                  </div>
                }
              />
            </List.Item>
          );
        }}
      />
    </div>
  );
}; 